import { cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../cn";

const filterToggleVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      active: {
        true: "border-primary/30 bg-primary-soft text-primary",
        false:
          "border-border/70 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
      },
    },
    defaultVariants: { active: false },
  },
);

export interface FilterToggleProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** Whether this filter is currently applied; drives both style and `aria-pressed`. */
  readonly active?: boolean;
}

/**
 * Pill-shaped toggle button for one-tap list filters (e.g. "Ongelezen",
 * "Gearchiveerd"). Exposes its state via `aria-pressed` so it reads correctly
 * to assistive technology; visually it swaps to the primary-soft treatment
 * when active.
 */
export const FilterToggle = forwardRef<HTMLButtonElement, FilterToggleProps>(
  ({ className, active = false, ...props }, ref) => (
    <button
      ref={ref}
      {...props}
      type="button"
      aria-pressed={active}
      className={cn(filterToggleVariants({ active }), className)}
    />
  ),
);
FilterToggle.displayName = "FilterToggle";

export { filterToggleVariants };
